import { createContext, useContext } from 'react';
import { ArrowDown, ArrowUp, Monitor, Smartphone, MonitorSmartphone, Trash2 } from 'lucide-react';
import ToggleSwitch from '../BaseComponents/Toggle/ToggleSwitch';
import { MAX_LABEL, MAX_URL } from './siteHeaderModel';
import { LANGS, SHOW_ON_LABELS, WEBSITE_DEFAULTS } from './headerDefaults';
import styles from './HeaderEditor.module.css';

// The small pieces every header card is built from, and the context they share: the preview
// language, the save-time validation errors, the holiday type list, and a way to point the live
// preview at the part being edited.

export const HeaderEditorContext = createContext({
  lang: 'nl',
  showErrors: false,
  errors: new Map(),
  holidayTypes: [],
  typesLoading: false,
  focusPreview: () => {},
});

export const useHeaderEditor = () => useContext(HeaderEditorContext);

/** The validation message for a field, once a save has been tried. */
export function useFieldError(path) {
  const { showErrors, errors } = useHeaderEditor();
  if (!showErrors || !path) return null;
  return errors.get(path) || null;
}

export function FieldError({ path, message }) {
  const fromPath = useFieldError(path);
  const text = message || fromPath;
  if (!text) return null;
  return <span className={styles.fieldError} role="alert">{text}</span>;
}

const OTHER = { nl: 'EN', en: 'NL' };

/**
 * One visitor-facing text in both languages, side by side. The placeholder is what the website
 * writes when the box is left empty: its own translated default where it has one, otherwise
 * the other language.
 *
 *   defaults  { nl, en } (or one string for both) the website falls back to; omit when none
 */
export function BilingualInput({
  label,
  value,
  onChange,
  defaults,
  placeholder = '',
  maxLength = MAX_LABEL,
  errorPath,
  hint,
  required = false,
  compact = false,
}) {
  const error = useFieldError(errorPath);
  const text = value || { nl: '', en: '' };
  const fallbackFor = (lang) => {
    if (defaults) return typeof defaults === 'string' ? defaults : defaults[lang] || '';
    const other = text[lang === 'nl' ? 'en' : 'nl'];
    if (other) return `Same as ${OTHER[lang]}: ${other}`;
    return placeholder;
  };
  return (
    <div className={`${styles.field} ${compact ? styles.fieldCompact : ''}`}>
      {label && (
        <label className={styles.fieldLabel}>
          {label}
          {required && <span className={styles.required} aria-hidden="true"> *</span>}
        </label>
      )}
      <div className={`${styles.biPair} ${error ? styles.invalid : ''}`}>
        {LANGS.map((lang) => (
          <div key={lang} className={styles.biCell}>
            <span className={`${styles.biTag} ${lang === 'en' ? styles.biTagEn : ''}`} aria-hidden="true">
              {lang.toUpperCase()}
            </span>
            <input
              value={text[lang] ?? ''}
              maxLength={maxLength}
              placeholder={fallbackFor(lang)}
              aria-label={`${label || 'Text'} (${lang.toUpperCase()})`}
              aria-invalid={error ? 'true' : undefined}
              onChange={(e) => onChange({ ...text, [lang]: e.target.value })}
            />
          </div>
        ))}
      </div>
      {error && <span className={styles.fieldError} role="alert">{error}</span>}
      {hint && !error && <span className={styles.hint}>{hint}</span>}
    </div>
  );
}

/** A plain one-line input with the editor's label, hint and error. */
export function TextField({ label, value, onChange, placeholder, errorPath, hint, maxLength, type = 'text', required, inputProps }) {
  const error = useFieldError(errorPath);
  return (
    <div className={styles.field}>
      {label && (
        <label className={styles.fieldLabel}>
          {label}
          {required && <span className={styles.required} aria-hidden="true"> *</span>}
        </label>
      )}
      <input
        className={`${styles.input} ${error ? styles.inputInvalid : ''}`}
        type={type}
        value={value ?? ''}
        placeholder={placeholder}
        maxLength={maxLength}
        aria-label={label}
        aria-invalid={error ? 'true' : undefined}
        onChange={(e) => onChange(e.target.value)}
        {...inputProps}
      />
      {error && <span className={styles.fieldError} role="alert">{error}</span>}
      {hint && !error && <span className={styles.hint}>{hint}</span>}
    </div>
  );
}

/** A link and its "open in a new tab" switch. */
export function LinkField({ label = 'Link', value, onChange, newTab, onNewTab, placeholder = '/page or https://', errorPath, hint, required }) {
  return (
    <div className={styles.linkField}>
      <TextField
        label={label}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        errorPath={errorPath}
        hint={hint}
        maxLength={MAX_URL}
        required={required}
      />
      {onNewTab && (
        <label className={styles.checkLabel}>
          <input type="checkbox" checked={Boolean(newTab)} onChange={(e) => onNewTab(e.target.checked)} />
          New tab
        </label>
      )}
    </div>
  );
}

/** A row of mutually exclusive buttons. */
export function Segmented({ value, options, onChange, ariaLabel, size = 'md' }) {
  return (
    <div className={`${styles.segmented} ${size === 'sm' ? styles.segmentedSm : ''}`} role="group" aria-label={ariaLabel}>
      {options.map((o) => {
        const Icon = o.icon;
        return (
          <button
            key={o.value}
            type="button"
            className={`${styles.segment} ${value === o.value ? styles.segmentOn : ''}`}
            aria-pressed={value === o.value}
            title={o.title || o.label}
            onClick={() => onChange(o.value)}
          >
            {Icon && <Icon size={13} aria-hidden="true" />}
            {o.label && <span>{o.label}</span>}
          </button>
        );
      })}
    </div>
  );
}

const SHOW_ON_OPTIONS = [
  { value: 'all', label: 'All', icon: MonitorSmartphone, title: SHOW_ON_LABELS.all },
  { value: 'desktop', label: 'Desktop', icon: Monitor, title: SHOW_ON_LABELS.desktop },
  { value: 'mobile', label: 'Mobile', icon: Smartphone, title: SHOW_ON_LABELS.mobile },
];

/** Which screens a block appears on. Mobile is the website's phone layout (900px and below). */
export function ShowOnSelect({ value, onChange }) {
  return <Segmented value={value || 'all'} options={SHOW_ON_OPTIONS} onChange={onChange} ariaLabel="Show on" size="sm" />;
}

/** A labelled on/off switch inside a card. */
export function SwitchRow({ title, description, checked, onChange, children }) {
  return (
    <div className={styles.switchRow}>
      <div className={styles.switchText}>
        <strong>{title}</strong>
        {description && <span>{description}</span>}
      </div>
      {children}
      <ToggleSwitch checked={Boolean(checked)} onChange={() => onChange(!checked)} />
    </div>
  );
}

/** Up, down and (optionally) remove for one row of a list. */
export function RowActions({ index, count, onMove, onRemove, removeTitle = 'Remove' }) {
  return (
    <div className={styles.rowActions}>
      <button type="button" className={styles.iconBtn} onClick={() => onMove(index, -1)} disabled={index === 0} title="Move up" aria-label="Move up">
        <ArrowUp size={14} />
      </button>
      <button type="button" className={styles.iconBtn} onClick={() => onMove(index, 1)} disabled={index === count - 1} title="Move down" aria-label="Move down">
        <ArrowDown size={14} />
      </button>
      {onRemove && (
        <button type="button" className={`${styles.iconBtn} ${styles.iconBtnDanger}`} onClick={() => onRemove(index)} title={removeTitle} aria-label={removeTitle}>
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
}

/** The website's own icon for a row left on Automatic, in the tile the menu draws it on. */
export function BuiltInTile({ icon: Icon, size = 34 }) {
  return (
    <span className={styles.builtInTile} style={{ width: size, height: size }} aria-hidden="true">
      {Icon ? <Icon size={Math.round(size * 0.46)} /> : null}
    </span>
  );
}

/**
 * One card of the editor. `enabled` / `showOn` appear only where the contract has them. A card
 * that is switched off stays editable, dimmed, so it can be prepared before it goes live.
 */
export function SectionCard({
  id,
  icon: Icon,
  tone = 'orange',
  title,
  description,
  enabled,
  onToggle,
  showOn,
  onShowOn,
  status,
  errorCount = 0,
  previewTarget,
  headerExtra,
  nested = false,
  children,
}) {
  const { focusPreview } = useHeaderEditor();
  const hasToggle = typeof enabled === 'boolean' && onToggle;
  const off = hasToggle && !enabled;
  return (
    <section
      id={id ? `header-section-${id}` : undefined}
      className={`${styles.section} ${nested ? styles.sectionNested : ''} ${errorCount ? styles.sectionError : ''}`}
      onFocusCapture={previewTarget ? () => focusPreview(previewTarget) : undefined}
      onMouseDown={previewTarget ? () => focusPreview(previewTarget) : undefined}
      aria-label={title}
    >
      <div className={styles.sectionHead}>
        <div className={styles.sectionTitle}>
          {Icon && (
            <div className={`${styles.sectionIcon} ${styles[`tone_${tone}`] || ''}`}>
              <Icon size={18} />
            </div>
          )}
          <div>
            <h3>
              {title}
              {errorCount > 0 && (
                <span className={styles.errorBadge}>{errorCount} to fix</span>
              )}
            </h3>
            {description && <p>{description}</p>}
          </div>
        </div>
        <div className={styles.sectionControls}>
          {status}
          {headerExtra}
          {onShowOn && <ShowOnSelect value={showOn} onChange={onShowOn} />}
          {hasToggle && (
            <span className={styles.toggleWrap} title={enabled ? 'Shown on the website' : 'Hidden on the website'}>
              <ToggleSwitch checked={enabled} onChange={() => onToggle(!enabled)} />
            </span>
          )}
        </div>
      </div>
      {off && <div className={styles.offNote}>Switched off: hidden on the website. You can still prepare it here.</div>}
      <div className={`${styles.sectionBody} ${off ? styles.bodyOff : ''}`}>{children}</div>
    </section>
  );
}

/** The "see all" row at the foot of a dropdown menu. */
export function FooterLinkEditor({ value, onChange, errorPath }) {
  const f = value || { enabled: false, label: { nl: '', en: '' }, url: '' };
  return (
    <div className={styles.subBlock}>
      <SwitchRow
        title={'"See all" row'}
        description="A last row at the bottom of the menu panel, e.g. to an overview page."
        checked={f.enabled}
        onChange={(enabled) => onChange({ ...f, enabled })}
      />
      {f.enabled && (
        <div className={styles.grid2}>
          <BilingualInput
            label="Row text"
            value={f.label}
            defaults={WEBSITE_DEFAULTS.seeAll}
            onChange={(label) => onChange({ ...f, label })}
          />
          <TextField
            label="Link"
            value={f.url}
            placeholder="/destinations"
            maxLength={MAX_URL}
            errorPath={errorPath}
            required
            onChange={(url) => onChange({ ...f, url })}
          />
        </div>
      )}
    </div>
  );
}

/** A small coloured state chip: on / off / scheduled and the like. */
export function StatusChip({ tone = 'muted', children, title }) {
  return (
    <span className={`${styles.chip} ${styles[`chip_${tone}`] || ''}`} title={title}>
      {children}
    </span>
  );
}
